import styled from 'styled-components';
import { Container } from 'ui';
import { decodeId, formatCurrencyLabel } from 'utils';
import { formatDateTime } from '@koob/dates';
import { useTranslation } from 'react-i18next';
import ToConnectionRequestState from 'components/ToConnectionRequestState';
import { useAsync } from 'react-async';
import { internalPut } from 'api/node';
import { useState } from 'react';
import DistributionOrganizationCard from './Partials/DistributionOrganizationCard';
import ExchangeRatesModal from './Partials/ExchangeRatesModal';
import { useApp, useNotifications, useSearchParams, useSnack } from 'hooks';
import LocationAwareSearch from 'components/LocationAwareSearch';
import { getToConnectionRequests } from 'api/node/toConnectionRequest';
import {
  KoobButton,
  KoobDataTable,
  KoobEntityChip,
  KoobInputSegmented,
  KoobInputSelect,
  KoobListPageLayout,
  KoobPageHeader,
  KoobTooltip,
  koobTableUrlSort,
} from '@koob/ui';
import { Link, useNavigate } from 'react-router-dom';
import { markNotificationAsRead } from 'api/node/notifications';
import NotificationType from 'constants/NotificationType';
import { getCurrencies } from 'api/node/currencies';
import { getOrganization, getOrganizations } from 'api/node/organization';
import { isEqual } from 'lodash';

const productTypes = [
  {
    type: 'hotel',
    path: 'hotels',
    notification: NotificationType.TO_CONNECTION_REQUEST_HOTEL_REQUESTED,
  },
  {
    type: 'experience',
    path: 'experiences',
    notification: NotificationType.TO_CONNECTION_REQUEST_EXPERIENCE_REQUESTED,
  },
  { type: 'template', path: 'templates' },
];

const DateWrapper = styled.span`
  text-transform: capitalize;
`;

const ToConnectionRequestList = () => {
  const { t } = useTranslation('toConnectionRequest');
  const navigate = useNavigate();
  const {
    isAllowedTo,
    canManageToConnectionRequest,
    user,
    currentOrganization,
    selectedDistributionOrganization,
    setSelectedDistributionOrganization,
  } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const { order, direction, search } = searchParams;
  const { sort, onSortChange } = koobTableUrlSort(
    searchParams,
    setSearchParams,
  );

  const { data, reload, isLoading } = useAsync({
    promiseFn: getToConnectionRequests,
    filters: {
      order,
      direction,
    },
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const [selectedOrganization, setSelectedOrganization] = useState({
    label: currentOrganization?.displayName,
    value: currentOrganization?.organizationId,
    currency: currentOrganization?.currency,
  });
  const { notify } = useSnack();
  const { notifications } = useNotifications();

  const { data: currenciesData, isLoading: currenciesLoading } = useAsync({
    promiseFn: getCurrencies,
  });
  const currenciesOptions = (currenciesData?.data || []).map(
    ({ name: currency }) => ({
      value: currency,
      label: formatCurrencyLabel(currency),
    }),
  );

  const { data: organizationDatas } = useAsync({
    promiseFn: getOrganizations,
    filters: {
      isPartner: false,
      discarded: false,
    },
  });

  const organizationOptions = (organizationDatas?.data ?? [])?.map(
    ({ id, name, currency }) => ({
      label: name,
      value: id,
      currency,
    }),
  );

  const activeOrganization =
    selectedDistributionOrganization ?? selectedOrganization;
  const { data: activeOrganizationData } = useAsync({
    promiseFn: getOrganization,
    id: Number(activeOrganization?.value),
    watch: activeOrganization?.value,
  });
  const [isExchangeRatesOpen, setIsExchangeRatesOpen] = useState(false);

  const handleOrganizationChange = (organization) => {
    setSelectedOrganization(organization);
    setSelectedDistributionOrganization(organization);
  };

  const handleToogle = async (value, request, kind) => {
    const payload =
      kind === 'cancelCondition'
        ? {
            partnerCancelCondition: value === 'to',
          }
        : {
            showToTa: value,
          };
    await internalPut(
      `/to-connection-requests/${request?.id}/quickModification`,
      payload,
    );
    reload();
    notify(t('updated'));
  };

  const modifyCurrency = async (value, request) => {
    await internalPut(
      `/to-connection-requests/${request?.id}/quickModification`,
      {
        currency: value,
      },
    );
    reload();
    notify(t('updatedTOCurrency'));
  };

  const readNotifications = (request, type) => {
    const notificationType = productTypes.find(
      (product) => product.type === type,
    )?.notification;
    if (notificationType) {
      readConnectionRequestNotifications(
        getNotificationsByProductType(request?.[type]?.id, notificationType),
      );
    }
  };

  const getNotificationsByProductType = (id, type) => {
    return notifications.filter(
      (notification) =>
        !notification.read &&
        notification.relatedObject === id &&
        notification.notificationType === type,
    );
  };

  const readConnectionRequestNotifications = async (notifications) => {
    if (!notifications?.length) {
      return;
    }

    for (const notification of notifications) {
      await markNotificationAsRead({ notificationId: notification?.id });
    }
  };

  const request = data?.data || [];
  const organizationIdToFilter = selectedOrganization.value;
  const formatResult = Object.values(request).reduce((acc, curr) => {
    if (
      curr.dmcOrganization?.id?.toString() !==
      decodeId(organizationIdToFilter)?.toString()
    ) {
      return acc; // Skip requests not related to the selected organization
    }

    const id = curr.toOrganization.id;
    const type = curr.type;
    const index = acc.findIndex((v) => v.toOrganization.id === id);
    if (index !== -1) {
      acc[index] = {
        ...acc[index],
        [type]: curr,
      };
    } else {
      acc = [...acc, { ...curr, [type]: curr }];
    }
    return acc;
  }, []);

  const isAllowedToManage = () => {
    return (
      isAllowedTo('canManageToConnectionRequest') ||
      canManageToConnectionRequest(user)
    );
  };

  const requests = (formatResult || []).filter((r) =>
    search
      ? r?.toOrganization?.name.toLowerCase().indexOf(search.toLowerCase()) > -1
      : true,
  );

  const columns = [
    {
      key: 'companyName',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.organisation')}
        </span>
      ),
      sortable: true,
      sortKey: 'organization',
      render: (request) => (
        <div className="max-w-xs">
          <KoobEntityChip
            rounded="md"
            name={request?.toOrganization?.name}
            image={request?.toOrganization?.avatarUrl}
            className="font-medium"
          />
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.created')}
        </span>
      ),
      width: '8rem',
      sortable: true,
      render: (request) => (
        <DateWrapper>
          {formatDateTime(
            request?.toOrganization?.toRequestAccess?.createdAt ??
              request?.toOrganization?.createdAt,
            'MMM DD, YYYY',
          )}
        </DateWrapper>
      ),
    },
    {
      key: 'products',
      label: (
        <KoobTooltip tip={t('headings.productsStatus')}>
          <span className="whitespace-normal text-center">
            {t('headings.products')}
          </span>
        </KoobTooltip>
      ),
      width: '8rem',
      render: (request) => (
        <div className="flex justify-center space-x-3">
          {productTypes.map(({ type, path }) => {
            const state = (
              <ToConnectionRequestState
                type={type}
                state={request?.[type]?.state}
                updating={!!request?.[type]?.activeJob}
              />
            );
            return request?.[type]?.id ? (
              <Link
                key={type}
                to={`${request[type].id}/${path}`}
                onClick={(event) => {
                  event.stopPropagation();
                  readNotifications(request, type);
                }}
              >
                {state}
              </Link>
            ) : (
              <span key={type}>{state}</span>
            );
          })}
        </div>
      ),
    },
    {
      key: 'apply_conditions_to',
      label: (
        <KoobTooltip tip={t('headings.contractingCondition')}>
          <span className="whitespace-normal text-center">
            {t('headings.condition')}
          </span>
        </KoobTooltip>
      ),
      width: '9rem',
      render: (request) => (
        // Using the control must not open the row.
        <div onClick={(event) => event.stopPropagation()}>
          <KoobInputSegmented
            value={request?.partnerCancelCondition ? 'to' : 'dmc'}
            onChange={(value) =>
              handleToogle(value, request, 'cancelCondition')
            }
            options={[
              { value: 'dmc', label: 'DMC' },
              { value: 'to', label: 'TO' },
            ]}
            disabled={!isAllowedToManage()}
          />
        </div>
      ),
    },
    {
      key: 'showToTa',
      label: (
        <KoobTooltip tip={t('headings.shareTOTA')}>
          <span className="whitespace-normal text-center">
            {t('headings.hotelkeeperVisibility')}
          </span>
        </KoobTooltip>
      ),
      width: '10rem',
      render: (request) => (
        // Using the control must not open the row.
        <div onClick={(event) => event.stopPropagation()}>
          <KoobInputSegmented
            value={request?.showToTa}
            onChange={(value) => handleToogle(value, request, 'ToTa')}
            options={[
              {
                label: t('show'),
                value: true,
              },
              {
                label: t('hide'),
                value: false,
              },
            ]}
            disabled={!isAllowedToManage()}
          />
        </div>
      ),
    },
    {
      key: 'currency',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.hotelCurrency')}
        </span>
      ),
      width: '11rem',
      render: (request) => (
        // Using the control must not open the row.
        <div onClick={(event) => event.stopPropagation()}>
          <KoobInputSelect
            onValueChange={(value) => {
              modifyCurrency(value, request?.hotel);
            }}
            options={currenciesOptions}
            loading={currenciesLoading}
            value={request?.hotel?.currency}
            disabled={!request?.hotel?.currency || !isAllowedToManage()}
          />
        </div>
      ),
    },
    {
      key: 'experienceCurrency',
      label: (
        <span className="whitespace-normal text-center">
          {t('headings.experienceCurrency')}
        </span>
      ),
      width: '11rem',
      render: (request) => (
        // Using the control must not open the row.
        <div onClick={(event) => event.stopPropagation()}>
          <KoobInputSelect
            onValueChange={(value) => {
              modifyCurrency(value, request?.experience);
            }}
            options={currenciesOptions}
            loading={currenciesLoading}
            value={request?.experience?.currency}
            disabled={!request?.experience?.currency || !isAllowedToManage()}
          />
        </div>
      ),
    },
  ];

  // The row opens its first distribution; the product icons open each one.
  const openRequest = (request) => {
    const product = productTypes.find(({ type }) => request?.[type]?.id);
    if (!product) {
      return;
    }
    readNotifications(request, product.type);
    navigate(`${request[product.type].id}/${product.path}`);
  };

  return (
    <>
      <KoobPageHeader
        title={t('pages:connections')}
        subtitle={data && t('misc:resultsCount', { count: requests.length })}
        actions={
          <KoobButton
            variant="secondary"
            onClick={() => setIsExchangeRatesOpen(true)}
          >
            <i className="fa-regular fa-gear mr-1.5" />
            {t('exchangeRates')}
          </KoobButton>
        }
      />

      <Container variant="main" size="full" className="gap-4">
        <DistributionOrganizationCard
          organization={activeOrganizationData?.data}
          organizations={organizationOptions}
          selectedOrganization={activeOrganization}
          onOrganizationChange={handleOrganizationChange}
          isAllowedToManage={isAllowedToManage()}
        />

        <KoobListPageLayout
          width="full"
          search={<LocationAwareSearch name="search" />}
          toolbarActions={
            isAllowedToManage() && (
              <Link to="/to-connection-request/bulk-edit">
                <KoobButton variant="primary">
                  <i className="far fa-list-check mr-1.5" />
                  {t('bulkEditButton')}
                </KoobButton>
              </Link>
            )
          }
        >
          <KoobDataTable
            columns={columns}
            data={requests}
            onRowClick={openRequest}
            loading={isLoading}
            minWidth={1000}
            sort={sort}
            onSortChange={onSortChange}
          />
        </KoobListPageLayout>
      </Container>

      <ExchangeRatesModal
        open={isExchangeRatesOpen}
        onClose={() => setIsExchangeRatesOpen(false)}
        dmcCurrency={activeOrganizationData?.data?.currency}
        isAllowedToManage={isAllowedToManage()}
        organizationId={activeOrganization?.value}
      />
    </>
  );
};

export default ToConnectionRequestList;
